import type { KeyValue } from '@angular/common';
import type { AbstractControl } from '@angular/forms';
import { GalleryItemType } from '@freelancer/ui/gallery-types';
import { decode } from 'blurhash';
import type { Assets } from '../assets';
import type { LinkTargetWhitelist } from '../link/link-new-tab-whitelist';
import {
  LINK_NEW_TAB_REGEXP_WHITELIST,
  LINK_NEW_TAB_WHITELIST,
  LINK_SAME_TAB_WHITELIST,
} from '../link/link-new-tab-whitelist';

/**
 * Returns a name / value object for each item in an object.
 * Like `Object.entries` but it returns a mapped object instead of an array
 */
export function entriesMap<V>(e: { [k: string]: V }): {
  name: string;
  value: V;
}[] {
  return Object.entries(e).map(([name, value]) => ({ name, value }));
}

export const literalsToArray = <const T extends string>(
  literals: readonly T[],
): readonly T[] => literals;

// File helpers

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  OTHERS = 'others',
  PDF = 'pdf',
}

export enum ImageFileType {
  BMP = 'bmp',
  GIF = 'gif',
  JPEG = 'jpeg',
  JPG = 'jpg',
  PNG = 'png',
  SVG = 'svg+xml',
  WEBP = 'webp',
}

export enum VideoFileType {
  MOV = 'quicktime',
  MP3 = 'mp3',
  MP4 = 'mp4',
  OGG = 'ogg',
  MPG = 'mpg',
  MPEG = 'mpeg',
  AVI = 'x-msvideo',
  FLV = 'x-flv',
}

export enum DocumentFileType {
  DOC = 'msword',
  DOCX = 'vnd.openxmlformats-officedocument.wordprocessingml.document',
  PDF = 'pdf',
  RTF = 'rtf',
  TXT = 'plain',
}

/**
 * File type is based on the file extension in filename
 * @param filename Filename with file extension
 */
export function getFileType(filename: string): FileType {
  if (isImageFile(filename)) {
    return FileType.IMAGE;
  }

  if (isVideoFile(filename)) {
    return FileType.VIDEO;
  }

  if (isPDF(filename)) {
    return FileType.PDF;
  }

  return FileType.OTHERS;
}

export function getGalleryItemType(
  fileType: FileType,
  fileName: string,
): GalleryItemType {
  switch (fileType) {
    case FileType.IMAGE:
      return GalleryItemType.PICTURE;
    case FileType.VIDEO:
      return GalleryItemType.VIDEO;
    default:
      if (isPDF(fileName)) {
        return GalleryItemType.PDF;
      }
      return GalleryItemType.FILE;
  }
}

export function isPDF(filename: string): boolean {
  const fileExtension = getFileExtension(filename);
  return fileExtension === 'pdf';
}

export function isImageFile(
  filename: string,
  options?: { allowedFileExtensions: string[] },
): boolean {
  const fileExtension = getFileExtension(filename);

  const fileExtensions = options?.allowedFileExtensions ?? [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'svg',
    'webp',
  ];

  return fileExtensions.includes(fileExtension);
}

// used to pick an icon or file-type label.
// NOT a playability check.
export function isVideoFile(filename: string): boolean {
  const fileExtension = getFileExtension(filename);

  return ['mp4', 'mov', 'ogg', 'avi', 'flv', 'webm'].includes(fileExtension);
}

// portfolio/showcase pipeline standardises on
// and generates thumbnails for. Gates the thumbnail + play-overlay UI (vs a
// generic file icon) in discover/showcase/portfolio flows.
export function isPlayableVideoFile(filename: string): boolean {
  const fileExtension = getFileExtension(filename);

  return ['mp4'].includes(fileExtension);
}

// formats a browser `<video>` element can natively decode across
// our target browsers. Used by messaging — both the inline tile (poster frame)
// and the media viewer rely on native decoding, so unsupported formats can't be
// thumbnailed or played without server-side thumbnails/transcoding. Adds webm
// over `isPlayableVideoFile` since messaging only needs browser-decodable, not
// the portfolio canonical format.
export function isInlinePreviewableVideoFile(filename: string): boolean {
  const fileExtension = getFileExtension(filename);

  return ['mp4', 'webm'].includes(fileExtension);
}

export function isAudioFile(filename: string): boolean {
  const fileExtension = getFileExtension(filename);

  return ['mp3'].includes(fileExtension);
}

export function isDocumentFile(filename: string): boolean {
  const fileExtension = getFileExtension(filename);

  return ['doc', 'pdf'].includes(fileExtension);
}

export function getFileExtension(filename: string): string {
  if (!filename) {
    return '';
  }
  const lowerCaseFilename = filename.toLowerCase();

  return lowerCaseFilename.split('.').pop() || '';
}

function hasMatchedRegexp(
  pattern: string,
  regExpList: readonly RegExp[],
): boolean {
  return regExpList.some(regExp => !!pattern.match(regExp));
}

export function isLinkWhitelisted(
  linkConfig: LinkTargetWhitelist,
  isExternalLink: boolean,
): boolean {
  const linkWhitelist = isExternalLink
    ? LINK_SAME_TAB_WHITELIST
    : LINK_NEW_TAB_WHITELIST;

  // Check internal link against whitelisted regex
  if (
    !isExternalLink &&
    hasMatchedRegexp(linkConfig.destination, LINK_NEW_TAB_REGEXP_WHITELIST)
  ) {
    return true;
  }

  const matches = linkWhitelist
    .filter(whitelistConfig =>
      linkConfig.destination.startsWith(whitelistConfig.destination),
    )
    .filter(
      whitelistConfig =>
        !whitelistConfig.source ||
        linkConfig.source?.startsWith(whitelistConfig.source),
    );

  return matches.length > 0;
}

export function trackByKey(_: number, v: KeyValue<string, any>): string {
  return v.key;
}

export function trackByValue(_: number, v: string | number): string | number {
  return v;
}

export function trackById(_: number, item: any): string | number {
  return item.id;
}

export function trackByIndex(index: number): number {
  return index;
}

export function trackByTitleProperty<T extends { title: string }>(
  _: number,
  object: T,
): string {
  return object.title;
}

export function trackByNameProperty<T extends { name: string }>(
  _: number,
  object: T,
): string {
  return object.name;
}

export function trackByValueProperty<T extends { value: string }>(
  _: number,
  object: T,
): string {
  return object.value;
}

export function trackBySrcProperty<T extends { src: string }>(
  _: number,
  object: T,
): string {
  return object.src;
}

export function trackByOptionProperty<T extends { option: string }>(
  _: number,
  object: T,
): string {
  return object.option;
}

export function trackByAbstractControl(
  _: number,
  control: AbstractControl,
): AbstractControl {
  return control;
}

export function generateUniqueID(): string {
  return Math.random().toString(36).substring(2, 6);
}

export function resolveAssetUrl(assets: Assets, src: string): string {
  // Check if the src is http(s), data:image, data:video, data:application/pdf, /api or protocol relative.
  if (
    !src ||
    src.startsWith('http://') ||
    src.startsWith('https://') ||
    src.startsWith('data:image/') ||
    src.startsWith('data:video/') ||
    src.startsWith('data:application/pdf') ||
    src.startsWith('/api') ||
    src.startsWith('//')
  ) {
    return src;
  }

  return assets.getUrl(src);
}

/**
 * Decodes a blurhash into a data URL suitable as an `<img>` placeholder while
 * the real image loads. Returns undefined if decoding fails or there is no
 * canvas available (e.g. during SSR).
 */
export function blurHashToDataUrl(
  blurHash: string,
  width = 80,
  height = 60,
): string | undefined {
  try {
    const pixels = decode(blurHash, width, height);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return undefined;
    }

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);

    return canvas.toDataURL();
  } catch {
    return undefined;
  }
}
